import { inject } from '@angular/core';
import { MetaDefinition } from '@angular/platform-browser';
import { ResolveFn } from '@angular/router';
import { ISbStoryData } from '@storyblok/js';
import { map } from 'rxjs';
import { LinkDefinition } from '../interfaces/link';
import { getFullPath } from '../lib/get-full-path';
import { getMetaDescriptionTags, getMetaImageTags, getMetaTitleTags, noIndexMetaTag } from '../lib/meta-helpers';
import { PICFLOW_CONFIG } from '../picflow-config';
import { StoryblokHelperService } from '../services/storyblok-helper.service';
import { customRenderText, fixHtml, removeHtml } from './custom-render-text';
import { getTitleBlockPlainText } from './lib/get-title-block-plain-text';
import { getStoryblokTranslatedPathLinks } from './lib/get-storyblok-translated-path-links';
import { convertToOgImageUrl } from './lib/meta-helpers';

export const storyblokPageMetaResolver: (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolver: any
) => ResolveFn<unknown> = resolver => route => {
  const helper = inject(StoryblokHelperService);
  const config = inject(PICFLOW_CONFIG);

  const getTitle = (story: ISbStoryData): string => {
    if (story.content.metaTitle) {
      return story.content.metaTitle;
    }
    // First Title block plain text (titleText richtext, else legacy title)
    const titleBlock = helper.getBlocks(story.content.body, ['title'], true).find(b => b?.titleText || b?.title);
    const fromBlock = getTitleBlockPlainText(titleBlock);
    if (fromBlock) return fromBlock;

    // otherwise return the story name as a backup
    return story.name;
  };

  const getDescription = (story: ISbStoryData): string => {
    if (story.content.metaDescription) return story.content.metaDescription;
    const block = helper.getBlocks(story.content.body, ['text'], true)[0];
    if (!block?.content) return;
    return removeHtml(fixHtml(customRenderText(block.content)));
  };

  const getImageUrl = (story: ISbStoryData): string => {
    if (story.content.keyVisual?.filename) {
      return story.content.keyVisual.filename;
    }
  };

  return resolver(route).pipe(
    map((story: ISbStoryData) => {
      if (!story) return;
      const tags: MetaDefinition[] = [];
      const links: LinkDefinition[] = [];

      const title = getTitle(story);
      const description = getDescription(story);
      const imageUrl = getImageUrl(story);

      tags.push(...getMetaTitleTags(title));
      tags.push(...getMetaDescriptionTags(description));

      if (imageUrl) {
        tags.push(...getMetaImageTags(convertToOgImageUrl(imageUrl, config.storyblokUrl)));
      }

      if (story.content.noIndex) {
        tags.push(noIndexMetaTag);
      }

      if (story.content.themeColor) {
        tags.push({ name: 'theme-color', content: story.content.themeColor });
      }

      links.push(...getStoryblokTranslatedPathLinks(story, config.correctPicflowUrl, getFullPath(route)));

      return { title, tags, links };
    })
  );
};
